import { motion } from "framer-motion";
import { LayoutDashboard, Shield, BarChart3, Settings, Activity, AlertTriangle, Users, ShieldCheck, Bot, Globe, User } from "lucide-react";

const sidebarItems = [
  { icon: LayoutDashboard, label: "Overview", active: true },
  { icon: Globe, label: "Domains", active: false },
  { icon: Shield, label: "Threats", active: false },
  { icon: BarChart3, label: "Analytics", active: false },
  { icon: Bot, label: "AI Traffic", active: false },
  { icon: Settings, label: "Settings", active: false },
  { icon: User, label: "Profile", active: false },
];

const metrics = [
  { label: "Requests Analyzed", value: "1.85M", icon: Activity, color: "text-primary", trend: "+12.3%" },
  { label: "Bots Blocked", value: "47.8K", icon: ShieldCheck, color: "text-destructive", trend: "-8.7%" },
  { label: "AI Crawlers Today", value: "1.2K", icon: Bot, color: "text-violet-400", trend: "+34%" },
  { label: "Threat Score", value: "73", icon: AlertTriangle, color: "text-amber", trend: "+5.2%" },
  { label: "Human Traffic", value: "82.4%", icon: Users, color: "text-green", trend: "+3.1%" },
];

const feedRows = [
  { ip: "185.220.101.34", type: "DDoS", country: "RU", action: "blocked", time: "2s ago" },
  { ip: "103.152.220.11", type: "Scraper", country: "CN", action: "blocked", time: "5s ago" },
  { ip: "44.228.64.12", type: "GPTBot", country: "US", action: "allowed", time: "6s ago" },
  { ip: "72.14.201.18", type: "Human", country: "US", action: "allowed", time: "7s ago" },
  { ip: "35.198.12.88", type: "ClaudeBot", country: "DE", action: "rate limited", time: "9s ago" },
  { ip: "45.148.10.78", type: "Credential Stuffing", country: "NL", action: "blocked", time: "12s ago" },
  { ip: "200.73.128.44", type: "Click Fraud", country: "BR", action: "rate limited", time: "15s ago" },
];

const chartBars = Array.from({ length: 30 }, (_, i) => {
  const human = 60 + Math.sin(i * 0.25) * 20 + Math.cos(i * 0.15) * 10;
  const bot = 20 + Math.sin(i * 0.3 + 1) * 8 + Math.cos(i * 0.4) * 5;
  const ai = 8 + Math.sin(i * 0.2 + 2) * 4 + Math.cos(i * 0.35) * 3;
  return { human: Math.max(10, human), bot: Math.max(5, bot), ai: Math.max(3, ai) };
});

const ProductShot = () => {
  return (
    <section className="relative py-24 md:py-32 px-6 overflow-hidden">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[600px] bg-primary/[0.04] blur-[180px] rounded-full pointer-events-none" />

      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="text-[10px] font-mono text-primary uppercase tracking-[0.3em] mb-4">The Dashboard</p>
          <h2 className="font-display font-extrabold text-4xl md:text-5xl lg:text-6xl text-foreground tracking-tight">
            Your command center.
          </h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="relative group"
        >
          <div
            className="relative glow-cyan-strong rounded-lg overflow-hidden border border-border/50 transition-transform duration-700 ease-out group-hover:[transform:perspective(2000px)_rotateX(0deg)]"
            style={{
              transform: "perspective(2000px) rotateX(2deg)",
              transformOrigin: "center top",
            }}
          >
            <div className="flex bg-card">
              {/* Sidebar — matches real dashboard */}
              <div className="hidden md:flex flex-col w-48 border-r border-border/50 bg-background/50">
                <div className="h-12 flex items-center px-4 border-b border-border/30">
                  <span className="font-display font-bold text-xs text-foreground">BotSweep<span className="text-primary">.</span></span>
                </div>
                <div className="p-2 space-y-0.5 flex-1">
                  {sidebarItems.map((item) => (
                    <div
                      key={item.label}
                      className={`flex items-center gap-2 px-3 py-2 rounded text-[10px] font-mono uppercase tracking-wider ${
                        item.active
                          ? "bg-primary/10 text-primary"
                          : "text-muted-foreground"
                      }`}
                    >
                      <item.icon className="w-3.5 h-3.5" />
                      {item.label}
                    </div>
                  ))}
                </div>
              </div>

              {/* Main content */}
              <div className="flex-1 p-4 md:p-5">
                {/* TopBar mockup */}
                <div className="flex items-center justify-between mb-5 pb-3 border-b border-border/30">
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-display font-semibold text-foreground">Overview</span>
                    <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-secondary/50 border border-border/30">
                      <span className="text-[8px] font-mono text-muted-foreground">mywebsite.com</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-green/10 border border-green/20">
                      <span className="w-1.5 h-1.5 rounded-full bg-green animate-pulse" />
                      <span className="text-[7px] font-data text-green uppercase tracking-widest">Live</span>
                    </div>
                  </div>
                </div>

                {/* Metric cards — matching real dashboard */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-5">
                  {metrics.map((m) => (
                    <div key={m.label} className="gradient-border-animated rounded-md p-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="p-1 rounded bg-secondary/50">
                          <m.icon className="w-2.5 h-2.5 text-muted-foreground" />
                        </div>
                        <span className={`text-[7px] font-mono ${m.trend.startsWith("+") ? "text-green" : "text-destructive"}`}>
                          {m.trend}
                        </span>
                      </div>
                      <div className={`font-data text-base md:text-lg font-bold ${m.color} tracking-wider`}>
                        {m.value}
                      </div>
                      <span className="text-[7px] font-mono text-muted-foreground uppercase tracking-wider">
                        {m.label}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Area chart — cyan and red like the real dashboard */}
                <div className="bg-background/30 border border-border/30 rounded-md p-3 mb-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[9px] font-mono text-foreground uppercase tracking-wider">Human vs Bot vs AI Traffic</span>
                    <span className="text-[8px] font-mono text-muted-foreground">Last 30 days</span>
                  </div>
                  <div className="h-28 flex items-end gap-[2px]">
                    {chartBars.map((bar, i) => (
                      <div key={i} className="flex-1 h-full flex flex-col-reverse gap-[1px]">
                        <div
                          className="rounded-sm"
                          style={{
                            height: `${bar.human}%`,
                            background: "linear-gradient(to top, hsl(191 100% 50% / 0.15), hsl(191 100% 50% / 0.4))",
                          }}
                        />
                        <div
                          className="rounded-sm"
                          style={{
                            height: `${bar.ai}%`,
                            background: "linear-gradient(to top, hsl(263 70% 60% / 0.2), hsl(263 70% 60% / 0.5))",
                          }}
                        />
                        <div
                          className="rounded-sm"
                          style={{
                            height: `${bar.bot}%`,
                            background: "linear-gradient(to top, hsl(346 100% 57% / 0.2), hsl(346 100% 57% / 0.5))",
                          }}
                        />
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center gap-4 mt-2">
                    <div className="flex items-center gap-1.5">
                      <span className="w-2 h-1 rounded-full bg-primary/50" />
                      <span className="text-[7px] font-mono text-muted-foreground">Human</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="w-2 h-1 rounded-full bg-violet-400/50" />
                      <span className="text-[7px] font-mono text-muted-foreground">AI</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="w-2 h-1 rounded-full bg-destructive/50" />
                      <span className="text-[7px] font-mono text-muted-foreground">Bot</span>
                    </div>
                  </div>
                </div>

                {/* Threat feed — matching real dashboard */}
                <div className="relative">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-destructive animate-pulse" />
                      <span className="text-[9px] font-mono text-foreground uppercase tracking-wider">Threat Feed</span>
                    </div>
                    <span className="text-[7px] font-mono text-muted-foreground">Real-time</span>
                  </div>
                  <div className="space-y-1">
                    {feedRows.map((row, i) => (
                      <motion.div
                        key={row.ip}
                        initial={{ opacity: 0, x: -10 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: i * 0.08 }}
                        className="flex items-center gap-3 px-3 py-1.5 bg-background/20 border border-border/20 rounded text-[9px] font-mono"
                      >
                        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                          row.action === "blocked" ? "bg-destructive" : row.action === "rate limited" ? "bg-amber" : "bg-green"
                        }`} />
                        <span className="text-foreground/80 w-24 hidden md:inline">{row.ip}</span>
                        <span className={
                          row.type === "GPTBot" || row.type === "ClaudeBot" ? "text-violet-400" :
                          row.action === "blocked" ? "text-destructive" : row.action === "rate limited" ? "text-amber" : "text-green"
                        }>
                          {row.type}
                        </span>
                        <span className="text-muted-foreground ml-auto">{row.country}</span>
                        <span className="text-muted-foreground/50 hidden md:inline">{row.time}</span>
                        <span className={`px-1.5 py-0.5 rounded text-[7px] uppercase tracking-wider ${
                          row.action === "blocked"
                            ? "bg-destructive/10 text-destructive"
                            : row.action === "rate limited"
                            ? "bg-amber/10 text-amber"
                            : "bg-green/10 text-green"
                        }`}>
                          {row.action}
                        </span>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Floating annotations */}
          <div className="hidden lg:block absolute -right-3 -top-3 z-10 bg-card/80 backdrop-blur-sm border border-violet-500/20 rounded px-3 py-1.5 text-[9px] font-mono text-violet-400">
            AI Traffic Controls ↗
          </div>
          <div className="hidden lg:block absolute -left-3 bottom-12 z-10 bg-card/80 backdrop-blur-sm border border-destructive/20 rounded px-3 py-1.5 text-[9px] font-mono text-destructive">
            ← One-click blocking
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ProductShot;
